/**
 * Status Utility Functions
 * 
 * Helper functions for formatting and styling status values.
 */

import { cn } from "@/lib/utils";

/**
 * Get status badge color classes
 */
export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case "aktif":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    case "dicabut":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
    case "nonaktif":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
  }
}

/**
 * Format status keanggotaan
 * Accepts number, string, null, or undefined
 */
export function formatStatusKeanggotaan(status: number | string | null | undefined): string {
  if (status === null || status === undefined) return "-";
  
  // Convert to number if it's a string
  const statusNum = typeof status === "string" ? parseInt(status, 10) : status;
  
  // Handle NaN case
  if (isNaN(statusNum)) return "-";
  
  switch (statusNum.toString()) {
    case "0":
      return "Belum anggota";
    case "1":
      return "Sudah anggota";
    case "2":
      return "Perpanjangan";
    default:
      return "-";
  }
}

/**
 * Get status keanggotaan badge color classes
 */
export function getStatusKeanggotaanColor(status: number | null): string {
  if (status === null || status === undefined) {
    return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
  }

  switch (status.toString()) {
    case "0":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
    case "1":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    case "2":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
  }
}

/**
 * Format IAR type for display
 */
export function formatIarType(tipeIar: string | undefined): string {
  if (!tipeIar) return "-";

  const normalized = tipeIar.toLowerCase().trim();

  switch (normalized) {
    case "iar_kehormatan":
      return "IAR KEHORMATAN";
    case "iar regular":
    case "iar_regular":
      return "IAR REGULAR";
    case "guest operator asing":
      return "Guest Operator Asing";
    case "iar warga negara asing":
      return "IAR Warga Negara Asing";
    default:
      // Return as-is if no mapping found (preserve original formatting)
      return tipeIar;
  }
}

/**
 * Get combined status badge classes
 */
export function getStatusBadgeClasses(...classes: string[]): string {
  return cn(
    "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium",
    ...classes
  );
}

